import { useEffect, useState } from 'react';
import { Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_PREFS,
  FAMILY_LABELS,
  askPermission,
  loadPrefs,
  permission,
  resetHighWater,
  savePrefs,
  supported,
} from '../../lib/browserNotify';

/**
 * Settings > Communications > Notification Center — "Desktop alerts".
 * (notify-center, 7 Sep 2026)
 *
 * Master switch (default ON for everyone, per Ruhshad), a checkbox per
 * notification family, and the mail chime sound toggle that already existed in
 * MailNotifyBridge — kept in the same block so there is one place to look.
 * The master switch being on does NOT by itself fire anything: the browser
 * permission still has to be granted, and this panel says so plainly.
 */

const CHIME_KEY = 'dcs.mail.chime';

const setChime = (on) => {
  try { localStorage.setItem(CHIME_KEY, on ? 'on' : 'off'); } catch (e) { /* private mode */ }
};

export default function DesktopAlertsSettings() {
  const auth = useAuth() || {};
  const userId = auth.user?.id;
  const [prefs, setPrefs] = useState(() => loadPrefs(userId));
  const [perm, setPerm] = useState(() => permission());
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/notify-center/prefs')
      .then((r) => {
        if (!alive || !r.data?.prefs) return;
        const merged = { ...DEFAULT_PREFS, ...r.data.prefs, types: { ...DEFAULT_PREFS.types, ...(r.data.prefs.types || {}) } };
        setPrefs(merged);
        savePrefs(userId, merged);
      })
      .catch(() => { /* module not installed yet — local defaults stand */ });
    return () => { alive = false; };
  }, [userId]);

  const persist = async (next) => {
    setPrefs(next);
    savePrefs(userId, next);
    setChime(!!next.sound);
    setSaving(true);
    try {
      await api.put('/notify-center/prefs', next);
      setNote('Saved');
    } catch (e) {
      setNote('Saved on this device only (server did not answer)');
    } finally {
      setSaving(false);
      setTimeout(() => setNote(''), 2500);
    }
  };

  const permLine = () => {
    if (!supported()) return 'This browser cannot show desktop alerts.';
    if (perm === 'granted') return 'Browser permission: granted — alerts will show.';
    if (perm === 'denied') {
      return 'Browser permission: blocked. Click the lock icon in the address bar → Notifications → Allow, then reload this page.';
    }
    return 'Browser permission: not asked yet.';
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4"
         data-testid="desktop-alerts-settings">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Bell className="w-4 h-4 text-slate-500" /> Desktop alerts
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">
            A real browser notification for every notification the ATS raises for you —
            even when this tab is in the background.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!prefs.browser_enabled}
          data-testid="desktop-alerts-master"
          onClick={() => persist({ ...prefs, browser_enabled: !prefs.browser_enabled })}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
            prefs.browser_enabled ? 'bg-emerald-600' : 'bg-slate-300'
          }`}
        >
          <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${
            prefs.browser_enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500">
        {perm === 'granted' ? <Bell className="w-3.5 h-3.5 text-emerald-600" />
          : <BellOff className="w-3.5 h-3.5 text-slate-400" />}
        <span>{permLine()}</span>
        {supported() && perm === 'default' && (
          <button
            type="button"
            data-testid="desktop-alerts-ask"
            className="text-xs font-medium text-blue-700 hover:underline"
            onClick={async () => setPerm(await askPermission())}
          >
            Turn on
          </button>
        )}
      </div>

      <fieldset disabled={!prefs.browser_enabled} className={prefs.browser_enabled ? '' : 'opacity-50'}>
        <legend className="text-xs font-medium text-slate-600 mb-2">Alert me about</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {FAMILY_LABELS.map(([id, label]) => (
            <label key={id} className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                data-testid={`desktop-alerts-type-${id}`}
                checked={prefs.types?.[id] !== false}
                onChange={(e) => persist({
                  ...prefs,
                  types: { ...prefs.types, [id]: e.target.checked },
                })}
                className="rounded border-slate-300"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="space-y-1.5 border-t border-slate-100 pt-3">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="desktop-alerts-always"
            checked={!!prefs.always_alert}
            onChange={(e) => persist({ ...prefs, always_alert: e.target.checked })}
            className="rounded border-slate-300"
          />
          Always alert, even while I am looking at this tab
          <span className="text-slate-400">(otherwise the in-app toast covers that)</span>
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            data-testid="desktop-alerts-sound"
            checked={prefs.sound !== false}
            onChange={(e) => persist({ ...prefs, sound: e.target.checked })}
            className="rounded border-slate-300"
          />
          {prefs.sound !== false ? <Volume2 className="w-3.5 h-3.5 text-slate-400" />
            : <VolumeX className="w-3.5 h-3.5 text-slate-400" />}
          Play the mail chime
          <span className="text-slate-400">(mail routed to your mailbox always chimes)</span>
        </label>
      </div>

      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          className="text-slate-500 hover:text-slate-800 underline"
          data-testid="desktop-alerts-reset"
          onClick={() => { resetHighWater(userId); setNote('This device will re-check from now on'); }}
        >
          Reset what this device has already alerted about
        </button>
        <span className="text-slate-400">{saving ? 'Saving…' : note}</span>
      </div>
    </div>
  );
}
